import { useEffect, useState } from 'react'

const MOBILE_QUERY = '(max-width: 767px)'
// Ignore tiny scroll jitters (momentum bounce, mobile address-bar resize).
const SCROLL_THRESHOLD = 8
// Always stay visible near the very top of the page.
const TOP_OFFSET = 24

/**
 * Tracks scroll position on mobile viewports so persistent chrome (header,
 * footer, legal ribbon) can hide once the user scrolls down, freeing up
 * screen space for form-heavy pages. It only reappears once the user has
 * scrolled back up to the top of the page — not on every scroll-up gesture.
 * Always returns `false` on viewports at or above the `md` breakpoint, so
 * desktop layout is untouched.
 */
export function useHideOnScroll() {
  const [hidden, setHidden] = useState(false)

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY)
    let isMobile = mediaQuery.matches
    let lastScrollY = window.scrollY
    let ticking = false

    const updateHiddenState = () => {
      ticking = false
      if (!isMobile) return

      const currentScrollY = window.scrollY
      const delta = currentScrollY - lastScrollY

      if (currentScrollY <= TOP_OFFSET) {
        // Only reveal once the user has scrolled all the way back to the top.
        setHidden(false)
        lastScrollY = currentScrollY
      } else if (delta > SCROLL_THRESHOLD) {
        setHidden(true)
        lastScrollY = currentScrollY
      }
      // Scrolling up without reaching the top leaves the current
      // shown/hidden state as-is, and deliberately does NOT move the
      // baseline: `lastScrollY` only advances once we've actually acted
      // on it (revealed at the top, or hidden past the threshold). If we
      // moved it on every sample instead, a slow scroll made up of many
      // sub-threshold deltas (e.g. 1-8px per animation frame) would never
      // accumulate past SCROLL_THRESHOLD, since each sample would reset
      // the baseline before the threshold was reached — so slow scrolling
      // would never hide the chrome at all.
    }

    const handleScroll = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(updateHiddenState)
      }
    }

    const handleMediaChange = (e: MediaQueryListEvent) => {
      isMobile = e.matches
      if (!isMobile) setHidden(false)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    mediaQuery.addEventListener('change', handleMediaChange)

    return () => {
      window.removeEventListener('scroll', handleScroll)
      mediaQuery.removeEventListener('change', handleMediaChange)
    }
  }, [])

  return hidden
}
